'use client';

import React from 'react';

import { MainSectionLayout } from '@/features/client/MainSectionLayout/MainSectionLayout';
import { MainSection } from '@/features/client/MainSection/MainSection';
import { Container } from '@/components/client/Container/Container';
import { Section } from '@/components/client/Section/Section';
import { ContactUsBanner } from '@/features/client/ContactUsBanner/ContactUsBanner';
import { ServiceCardsSection } from '@/features/client/ServiceCardsSection/ServiceCardsSection';
import { ContentLayout } from '@/features/client/Services/components/ContentLayout/ContentLayout';
import { RequestForm } from '@/features/client/Forms/RequestForm/RequestForm';
import { ServicesNavMenu } from '@/features/client/Services/components/ServicesNavMenu/ServicesNavMenu';
import { Content } from '@/features/client/Services/components/Content/Content';
import image from '@/public/images/services/main_page/plumbing_solution.webp';

const data = {
  title: 'Plumbing Expertise',
  description: `At Tytum, we're dedicated to innovation and your home comfort. We aim to keep you comfortable year-round with energy-efficient and built to last solutions.\n\nWith years of experience, we provide a full range of plumbing services to address all your needs, whether residential or commercial. Our skilled professionals are equipped to handle any plumbing issue, big or small, ensuring reliable and efficient solutions.`,
  image: image,
  offer_title: 'Plumbing Services We Offer:',
  offer_data: [
    {
      title: 'General Plumbing Repairs:',
      list: [
        { title: '    Leak Detection and Repair' },
        { title: 'Pipe Repair and Replacement' },
        { title: 'Faucet and Fixture Repair' },
        { title: 'Toilet Repair and Installation' },
        { title: 'Garbage Disposal Repair and Installation' },
        { title: 'Shower and Bathtub Repair' },
      ],
    },

    {
      title: 'Drain Cleaning Services: ',
      list: [
        { title: '    Clogged Drain Cleaning' },
        { title: '    Hydro Jetting Services' },
        { title: '    Drain Line Repair and Replacement' },
        { title: '    Sewer Line Cleaning and Repair' },
      ],
    },

    {
      title: 'Water Heater Services:    ',
      list: [
        { title: '    Water Heater Repair and Maintenance' },
        { title: 'Water Heater Installation and Replacement' },
        { title: 'Tankless Water Heater Services' },
        {
          title:
            'In addition to water heating solutions, we also help integrate <a href="https://tytum.com/services/heating-and-air/cooling-and-heating">ductless cooling & heating</a> systems that deliver year-round comfort and efficiency without the need for bulky ductwork.',
        },
      ],
    },

    {
      title: 'Sewer Services:   ',
      list: [
        { title: '    Sewer Line Inspection and Repair' },
        { title: 'Sewer Line Replacement' },
        { title: 'Trenchless Sewer Repair' },
        { title: 'Sewer Camera Inspections' },
      ],
    },

    {
      title: 'Pipe Services:   ',
      list: [
        { title: '    Repiping and Pipe Replacement' },
        { title: 'Copper and PEX Repiping' },
        {
          title:
            'Pipe Insulation Services. We also support builders and property owners with <a href="https://tytum.com/services/construction">new construction services</a>, providing complete plumbing system installation for residential and commercial projects from the ground up.',
        },
      ],
    },

    {
      title: 'Emergency Plumbing Services:   ',
      list: [
        { title: '    24/7 Emergency Plumbing Repairs' },
        { title: 'Burst Pipe Repair' },
        { title: 'Emergency Leak Repair' },
        { title: 'Emergency Drain Cleaning' },
      ],
    },

    {
      title: 'Bathroom and Kitchen Plumbing:    ',
      list: [
        { title: '    Sink and Faucet Installation' },
        { title: 'Garbage Disposal Installation' },
        { title: 'Dishwasher Installation' },
        { title: 'Ice Maker Line Installation' },
        { title: 'Shower and Bathtub Installation' },
        {
          title:
            'Toilet Installation and Replacement: Many remodeling projects require coordination beyond plumbing, which is why we often work side by side with <a href="https://tytum.com/services/electrical">electrical services</a> to ensure kitchens and bathrooms are both safe and fully functional.',
        },
      ],
    },

    {
      title: 'Water Filtration and Softening:   ',
      list: [
        { title: '    Whole-House Water Filtration Systems' },
        { title: 'Water Softener Installation and Repair' },
        { title: 'Reverse Osmosis Systems' },
        { title: 'Water Quality Testing' },
      ],
    },

    {
      title: 'Gas Line Services:   ',
      list: [
        { title: '    Gas Line Installation and Repair' },
        { title: '    Gas Leak Detection' },
        { title: '    Gas Appliance Hookups' },
      ],
    },

    {
      title: 'Plumbing Inspections and Maintenance:   ',
      list: [
        { title: '    Annual Plumbing System Inspections' },
        { title: '    Preventative Plumbing Maintenance' },
        { title: '    Home Plumbing Inspections for Real Estate' },
      ],
    },
  ],
  banner_title: `“Ensuring Your Home's Vital Flow”`,
  banner_description:
    'Experience the reliability of Tytum expert plumbing services. From minor fixes to major installations, we keep your plumbing running smoothly with precision and care.',
  bookServiceButton: true,
  why_title: 'Why Choose Tytum for Plumbing Solutions?',
  why_description:
    'We know how crucial it is to promptly and effectively address problems to ensure the safety and functionality of your home. We deliver quality results on time and within budget, turning your dream home into a reality. For larger renovation projects, our plumbing team frequently collaborates with experts in <a href="https://tytum.com/services/remodeling">remodeling services in San Diego</a>, ensuring every upgrade enhances both style and functionality.',
  why_items: [
    {
      title: 'Expert Plumbers',
      description:
        'Our team is fully licensed, insured, and equipped to tackle any plumbing job, big or small.',
    },
    {
      title: 'Exceptional Quality: ',
      description:
        'We take pride in delivering top-notch plumbing services with precision and care.',
    },
    {
      title: 'Customer-Centric Approach:',
      description:
        'Your satisfaction is our priority. We work closely with you to ensure your plumbing needs are met with professionalism and transparency.',
    },
  ],
  why_footerText:
    'Trust us to be your go-to provider in the San Diego area and ensure the comfort and safety of your home. Call us or fill out our online form for questions or a free quote.',
};

const PlumbingSolutionsPage = () => {
  return (
    <div>
      <MainSectionLayout variant="primary" layout="sline">
        <MainSection
          label="Service Details"
          title="Plumbing"
          description="We make plumbing problems easy to solve so you can enjoy peace of mind.
From leaky faucets to major repairs, we've got you covered."
          withContactButton
        />
      </MainSectionLayout>

      <Section variant="secondary">
        <Container>
          <ContentLayout
            contentLeft={
              <>
                <ServicesNavMenu />
                <RequestForm />
              </>
            }
            contentRight={<Content data={data} />}
          />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ServiceCardsSection shortVariant={true} />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ContactUsBanner phone="+1(858) 289-4881 " />
        </Container>
      </Section>
    </div>
  );
};

export default PlumbingSolutionsPage;
